import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { escapeHtml, openPrintWindow } from '../../utils/printUtils';
import {
  Wheat,
  Plus,
  RefreshCw,
  CheckCircle2,
  Users,
  Calendar,
  X,
  Edit,
  Trash2,
  Receipt,
  Download,
  Search,
  User,
  UserPlus,
  Printer,
  Eye,
  PoundSterling,
  Lock,
  FileSpreadsheet,
  Filter,
  ArrowUpRight,
  FileText
} from 'lucide-react';

import { QRCodeSVG } from 'qrcode.react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

// Only Juvenile and Adult Harvest types
const HARVEST_TYPES = [
  { value: 'adult', label: 'Adult Harvest', color: 'emerald' },
  { value: 'juvenile', label: 'Juvenile Harvest', color: 'blue' }
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'pos', label: 'POS' }
];

// Church logo URL
const CHURCH_LOGO_URL = "/images/church-logo.png?v=2";

// Receipt Component for printing
const ReceiptModal = ({ receipt, miniStatement, church, onClose, onPrint }) => {
  const receiptRef = useRef(null);
  
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const prevTotal = miniStatement?.previous_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00';
    const curContrib = miniStatement?.current_contribution?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00';
    const totalDate = miniStatement?.total_to_date?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00';
    const amountFormatted = receipt.amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00';
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt - ${receipt.receipt_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Georgia', serif; background: #fff; }
            .receipt { max-width: 420px; margin: 0 auto; padding: 30px; position: relative; }
            .watermark { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.08; width: 200px; height: 200px; pointer-events: none; z-index: 0; }
            .watermark img { width: 200px; height: 200px; object-fit: contain; }
            .content { position: relative; z-index: 1; }
            .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 15px; margin-bottom: 20px; }
            .church-logo { width: 80px; height: 80px; margin: 0 auto 10px; border-radius: 50%; overflow: hidden; }
            .church-logo img { width: 80px; height: 80px; object-fit: contain; }
            .church-name { font-size: 16px; font-weight: bold; color: #1e3a5f; text-transform: uppercase; letter-spacing: 1px; }
            .church-address { font-size: 12px; color: #666; margin-top: 4px; }
            .receipt-badge { display: inline-block; margin-top: 12px; padding: 4px 16px; background: #eff6ff; border-radius: 20px; font-size: 11px; font-weight: bold; color: #1e3a5f; text-transform: uppercase; letter-spacing: 1px; }
            .receipt-num-box { background: #f8fafc; border-radius: 10px; padding: 10px; margin-bottom: 16px; text-align: center; }
            .receipt-num-label { font-size: 11px; color: #94a3b8; }
            .receipt-num-value { font-family: monospace; font-weight: bold; color: #1e3a5f; font-size: 14px; }
            .detail-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #e2e8f0; }
            .detail-label { color: #64748b; font-size: 12px; }
            .detail-value { font-weight: 600; color: #1e293b; font-size: 12px; }
            .id-badge { font-family: monospace; background: #dbeafe; color: #1d4ed8; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
            .amount-box { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); color: white; padding: 16px; border-radius: 10px; margin: 16px 0; text-align: center; }
            .amount-label { font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 1px; }
            .amount-value { font-size: 28px; font-weight: bold; margin-top: 4px; }
            .statement { background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 14px; margin-bottom: 16px; }
            .statement-title { font-size: 11px; font-weight: bold; color: #92400e; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; }
            .statement-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; color: #92400e; }
            .statement-total { border-top: 2px solid #d97706; margin-top: 8px; padding-top: 8px; font-weight: bold; color: #78350f; }
            .footer { text-align: center; margin-top: 16px; padding-top: 14px; border-top: 1px solid #e2e8f0; }
            .thank-you { color: #1e3a5f; font-style: italic; font-size: 13px; }
            .timestamp { font-size: 10px; color: #94a3b8; margin-top: 8px; }
            @media print {
              body { padding: 0; margin: 0; }
              .receipt { padding: 15px; max-width: 100%; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="watermark">
              <img src="${CHURCH_LOGO_URL}" alt="" />
            </div>
            <div class="content">
              <div class="header">
                <div class="church-logo">
                  <img src="${CHURCH_LOGO_URL}" alt="Church Logo" />
                </div>
                <div class="church-name">${escapeHtml(church?.name || 'Celestial Church of Christ — Valley of Berachah (VOB) Parish')}</div>
                <div class="church-address">${escapeHtml(church?.address || '62 Spital Street, Dartford DA1 2DT')}</div>
                <div class="receipt-badge">Harvest Contribution Receipt</div>
              </div>

              <div class="receipt-num-box">
                <div class="receipt-num-label">Receipt Number</div>
                <div class="receipt-num-value">${escapeHtml(receipt.receipt_number)}</div>
              </div>

              <div class="detail-row">
                <span class="detail-label">Date</span>
                <span class="detail-value">${escapeHtml(receipt.date)}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Contributor ID</span>
                <span class="id-badge">${escapeHtml(receipt.contributor_id_display || receipt.contributor_id || '-')}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Contributor</span>
                <span class="detail-value">${escapeHtml(receipt.contributor_name)}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Project Type</span>
                <span class="detail-value" style="text-transform:capitalize;">${escapeHtml(receipt.harvest_type)} Harvest</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Payment Method</span>
                <span class="detail-value" style="text-transform:capitalize;">${escapeHtml((receipt.payment_method || '').replace('_', ' '))}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Received By</span>
                <span class="detail-value">${escapeHtml(receipt.received_by_name || 'Admin')}</span>
              </div>

              <div class="amount-box">
                <div class="amount-label">Amount Contributed</div>
                <div class="amount-value">£${amountFormatted}</div>
              </div>

              ${miniStatement ? `
              <div class="statement">
                <div class="statement-title">Contribution Statement</div>
                <div class="statement-row">
                  <span>Previous Contributions</span>
                  <span>£${prevTotal}</span>
                </div>
                <div class="statement-row">
                  <span>This Contribution</span>
                  <span>£${curContrib}</span>
                </div>
                <div class="statement-row statement-total">
                  <span>Total to Date</span>
                  <span>£${totalDate}</span>
                </div>
              </div>
              ` : ''}

              <div class="footer">
                <div class="thank-you">Thank you for your generous contribution!</div>
                <div class="timestamp">Generated: ${new Date().toLocaleString('en-GB')}</div>
                <div class="timestamp">This is a computer-generated receipt</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Contribution Receipt
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 overflow-y-auto max-h-[70vh]" ref={receiptRef}>
          {/* Watermark - Church Logo */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-[0.08] pointer-events-none select-none" style={{width: '200px', height: '200px'}}>
            <img src={CHURCH_LOGO_URL} alt="" className="w-full h-full object-contain" />
          </div>
          
          {/* Header */}
          <div className="text-center border-b-2 border-blue-900 pb-4 mb-4 relative">
            <div className="w-20 h-20 mx-auto mb-2">
              <img src={CHURCH_LOGO_URL} alt="Church Logo" className="w-full h-full object-contain rounded-full" />
            </div>
            <h2 className="text-lg font-bold text-blue-900 uppercase tracking-wider">
              {church?.name || 'Celestial Church of Christ'}
            </h2>
            <p className="text-sm text-slate-600 mt-1">{church?.address || 'Valley of Berachah Parish'}</p>
            <div className="mt-3 inline-block px-4 py-1 bg-blue-50 rounded-full">
              <p className="text-xs font-bold text-blue-900 uppercase tracking-wide">Harvest Contribution Receipt</p>
            </div>
          </div>

          {/* Receipt Number */}
          <div className="bg-slate-50 rounded-xl p-3 mb-4 text-center">
            <p className="text-xs text-slate-500">Receipt Number</p>
            <p className="font-mono font-bold text-blue-900">{receipt.receipt_number}</p>
          </div>

          {/* Details */}
          <div className="space-y-2 mb-4">
            <div className="flex justify-between py-2 border-b border-dashed border-slate-200">
              <span className="text-sm text-slate-600">Date</span>
              <span className="text-sm font-semibold">{receipt.date}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-dashed border-slate-200">
              <span className="text-sm text-slate-600">Contributor ID</span>
              <span className="text-sm font-mono font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                {receipt.contributor_id_display || receipt.contributor_id || '-'}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-dashed border-slate-200">
              <span className="text-sm text-slate-600">Contributor</span>
              <span className="text-sm font-semibold">{receipt.contributor_name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-dashed border-slate-200">
              <span className="text-sm text-slate-600">Project Type</span>
              <span className="text-sm font-semibold capitalize">{receipt.harvest_type} Harvest</span>
            </div>
            <div className="flex justify-between py-2 border-b border-dashed border-slate-200">
              <span className="text-sm text-slate-600">Payment Method</span>
              <span className="text-sm font-semibold capitalize">{receipt.payment_method?.replace('_', ' ')}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-dashed border-slate-200">
              <span className="text-sm text-slate-600">Received By</span>
              <span className="text-sm font-semibold">{receipt.received_by_name || 'Admin'}</span>
            </div>
          </div>

          {/* Amount */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-xl p-4 mb-4 text-center">
            <p className="text-xs opacity-80 uppercase tracking-wide">Amount Contributed</p>
            <p className="text-3xl font-bold mt-1">£{receipt.amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
          </div>

          {/* Mini Statement */}
          {miniStatement && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                <PoundSterling className="w-4 h-4" />
                Contribution Statement
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-amber-700">Previous Contributions</span>
                  <span className="font-medium">£{miniStatement.previous_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-700">This Contribution</span>
                  <span className="font-medium">£{miniStatement.current_contribution?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t-2 border-amber-300 mt-2">
                  <span className="font-bold text-amber-900">Total to Date</span>
                  <span className="font-bold text-amber-900">£{miniStatement.total_to_date?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-4 border-t border-slate-200">
            <p className="text-blue-900 italic text-sm">Thank you for your generous contribution!</p>
            <p className="text-xs text-slate-400 mt-2">
              Generated: {new Date().toLocaleString('en-GB')}
            </p>
            <p className="text-xs text-slate-400">This is a computer-generated receipt</p>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-900 to-blue-700 text-white font-bold flex items-center justify-center gap-2 hover:from-blue-800 hover:to-blue-600 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print Receipt
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Contribution Reports Component
const ContributionReports = ({ events, contributions, members, guests }) => {
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [sortBy, setSortBy] = useState('amount');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Get contributions for selected event
  const getFilteredContributions = () => {
    let filtered = [...contributions];
    
    // Filter by event
    if (selectedEvent !== 'all') {
      filtered = filtered.filter(c => c.harvest_event_id === selectedEvent);
    }
    
    // Filter by search
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.contributor_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort contributions
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'amount') {
        comparison = (a.amount || 0) - (b.amount || 0);
      } else if (sortBy === 'name') {
        comparison = (a.contributor_name || '').localeCompare(b.contributor_name || '');
      } else if (sortBy === 'date') {
        comparison = new Date(a.date || 0) - new Date(b.date || 0);
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });
    
    return filtered;
  };

  // Group contributions by contributor to show total per person
  const getContributorSummary = () => {
    const filtered = getFilteredContributions();
    const summary = {};
    
    filtered.forEach(c => {
      const name = c.contributor_name || 'Unknown';
      if (!summary[name]) {
        // Get contributor ID based on type
        let contributorId = '-';
        if (c.contributor_type === 'member') {
          const member = members.find(m => m.id === c.contributor_id);
          contributorId = member?.member_number || '-';
        } else {
          const guest = guests.find(g => g.contributor_id === c.contributor_id);
          contributorId = guest?.contributor_id || c.contributor_id || '-';
        }
        
        summary[name] = {
          name,
          total: 0,
          count: 0,
          contributorType: c.contributor_type,
          contributorId: contributorId,
          contributions: []
        };
      }
      summary[name].total += c.amount || 0;
      summary[name].count += 1;
      summary[name].contributions.push(c);
    });
    
    // Convert to array and sort
    let summaryArray = Object.values(summary);
    summaryArray.sort((a, b) => {
      if (sortBy === 'amount') {
        return sortOrder === 'desc' ? b.total - a.total : a.total - b.total;
      } else {
        return sortOrder === 'desc' 
          ? b.name.localeCompare(a.name) 
          : a.name.localeCompare(b.name);
      }
    });
    
    return summaryArray;
  };

  const filteredContributions = getFilteredContributions();
  const contributorSummary = getContributorSummary();
  const totalAmount = filteredContributions.reduce((sum, c) => sum + (c.amount || 0), 0);

  // Get event name by ID
  const getEventName = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event?.name || 'Unknown Event';
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['ID', 'Name', 'Type', 'Total Contributed', 'Number of Contributions'];
    const rows = contributorSummary.map(s => [
      s.contributorId,
      s.name,
      s.contributorType === 'member' ? 'Member' : 'Guest',
      `£${s.total.toFixed(2)}`,
      s.count
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `church-project-contributions-${selectedEvent === 'all' ? 'all-projects' : selectedEvent}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print report
  const printReport = () => {
    const styles = `
      body { font-family: Arial, sans-serif; padding: 20px; }
      h1 { color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 10px; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
      th { background: #fef3c7; color: #92400e; }
      .total-row { background: #fef3c7; font-weight: bold; }
      .summary { margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; }
    `;

    const bodyHtml = `
      <h1>Harvest Contribution Report</h1>
      <p><strong>Event:</strong> ${escapeHtml(selectedEvent === 'all' ? 'All Events' : getEventName(selectedEvent))}</p>
      <p><strong>Generated:</strong> ${escapeHtml(new Date().toLocaleString())}</p>
      <div class="summary">
        <p><strong>Total Contributors:</strong> ${contributorSummary.length}</p>
        <p><strong>Total Contributions:</strong> ${filteredContributions.length}</p>
        <p><strong>Total Amount:</strong> £${totalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
      </div>
      <table>
        <thead>
          <tr><th>#</th><th>Contributor Name</th><th>Type</th><th>Total Contributed</th><th>No. of Contributions</th></tr>
        </thead>
        <tbody>
          ${contributorSummary.map((s, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${escapeHtml(s.name)}</td>
              <td>${s.contributorType === 'member' ? 'Member' : 'Guest'}</td>
              <td>£${s.total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
              <td>${s.count}</td>
            </tr>
          `).join('')}
          <tr class="total-row">
            <td colspan="3">TOTAL</td>
            <td>£${totalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
            <td>${filteredContributions.length}</td>
          </tr>
        </tbody>
      </table>
    `;

    openPrintWindow('Harvest Contribution Report', styles, bodyHtml);
  };

  return (
    <div className="space-y-6">
      {/* Report Controls */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Contribution Reports</h3>
            <p className="text-sm text-slate-500">View who has contributed to harvest events</p>
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-100 text-emerald-700 font-medium hover:bg-emerald-200 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </motion.button>
            <motion.button
              onClick={printReport}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-100 text-amber-700 font-medium hover:bg-amber-200 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Printer className="w-4 h-4" />
              Print Report
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Select Project</label>
            <select
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-400 outline-none"
              value={selectedEvent}
              onChange={e => setSelectedEvent(e.target.value)}
            >
              <option value="all">All Events</option>
              {events.map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Sort By</label>
            <select
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-400 outline-none"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="amount">Amount</option>
              <option value="name">Name</option>
              <option value="date">Date</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Order</label>
            <select
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-400 outline-none"
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
            >
              <option value="desc">Highest First</option>
              <option value="asc">Lowest First</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search contributor..."
                className="w-full border-2 border-slate-200 rounded-xl pl-10 pr-4 py-2.5 focus:border-amber-400 outline-none"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-amber-500">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-900">{contributorSummary.length}</p>
              <p className="text-sm text-amber-700">Total Contributors</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-emerald-500">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-900">{filteredContributions.length}</p>
              <p className="text-sm text-emerald-700">Total Contributions</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-blue-500">
              <PoundSterling className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-900">£{totalAmount.toLocaleString('en-GB')}</p>
              <p className="text-sm text-blue-700">Total Amount</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contributors Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-amber-50 to-yellow-50">
                <th className="text-left py-4 px-6 font-semibold text-slate-700">#</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">ID</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Contributor</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Type</th>
                <th className="text-right py-4 px-6 font-semibold text-slate-700">Total Contributed</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Contributions</th>
              </tr>
            </thead>
            <tbody>
              {contributorSummary.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-500">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No contributions found for the selected criteria</p>
                  </td>
                </tr>
              ) : (
                contributorSummary.map((contributor, index) => (
                  <motion.tr
                    key={contributor.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-t border-slate-100 hover:bg-amber-50/50 transition-colors"
                  >
                    <td className="py-4 px-6 text-slate-600">{index + 1}</td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-lg text-xs font-mono font-medium ${
                        contributor.contributorType === 'member' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {contributor.contributorId || '-'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${contributor.contributorType === 'member' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                          {contributor.contributorType === 'member' ? (
                            <User className="w-4 h-4 text-blue-600" />
                          ) : (
                            <UserPlus className="w-4 h-4 text-purple-600" />
                          )}
                        </div>
                        <span className="font-medium text-slate-800">{contributor.name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        contributor.contributorType === 'member' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {contributor.contributorType === 'member' ? 'Member' : 'Guest'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <span className="font-bold text-emerald-600">
                        £{contributor.total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                        {contributor.count}
                      </span>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
            {contributorSummary.length > 0 && (
              <tfoot>
                <tr className="bg-gradient-to-r from-amber-100 to-yellow-100 font-bold">
                  <td colSpan={4} className="py-4 px-6 text-slate-800">TOTAL</td>
                  <td className="py-4 px-6 text-right text-emerald-700">
                    £{totalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-4 px-6 text-center text-slate-700">{filteredContributions.length}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const HarvestSection = ({ adminUser }) => {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [members, setMembers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showContributionForm, setShowContributionForm] = useState(false);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterEventId, setFilterEventId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showReceipt, setShowReceipt] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [church, setChurch] = useState(null);

  const isMainAdmin = adminUser?.role === 'main_admin';

  const [voidModal, setVoidModal] = useState({ show: false, contribution: null, reason: '', loading: false });

  const handleVoidContribution = async () => {
    if (!voidModal.reason.trim()) {
      alert('Please enter a reason for voiding this contribution.');
      return;
    }
    setVoidModal(prev => ({ ...prev, loading: true }));
    try {
      await axios.post(`${API}/harvest-contributions/${voidModal.contribution.id}/void`, {
        reason: voidModal.reason.trim(),
        admin_role: adminUser?.role || ''
      });
      setVoidModal({ show: false, contribution: null, reason: '', loading: false });
      loadContributions();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      if (status === 404) {
        alert('Void endpoint not found. Please deploy the latest version.');
      } else {
        alert(detail || 'Failed to void contribution. Please try again.');
      }
      setVoidModal(prev => ({ ...prev, loading: false }));
    }
  };

  const [eventForm, setEventForm] = useState({
    name: '',
    harvest_type: 'adult',
    date: new Date().toISOString().split('T')[0],
    description: '',
    target_amount: 0
  });

  // Disbursement state
  const [disbursements, setDisbursements] = useState([]);
  const [showDisbursementForm, setShowDisbursementForm] = useState(false);
  const [disbursementForm, setDisbursementForm] = useState({
    harvest_event_id: '',
    amount: '',
    requested_by: '',
    purpose: ''
  });
  const [disbursementLoading, setDisbursementLoading] = useState(false);

  const loadDisbursements = async () => {
    try {
      const res = await axios.get(`${API}/harvest-disbursements`);
      setDisbursements(res.data);
    } catch (e) { console.error('Error loading disbursements:', e); }
  };

  useEffect(() => { loadDisbursements(); }, []);

  const handleDisbursement = async () => {
    if (!disbursementForm.harvest_event_id) { alert('Please select a project'); return; }
    if (!disbursementForm.amount || parseFloat(disbursementForm.amount) <= 0) { alert('Please enter a valid amount'); return; }
    if (!disbursementForm.requested_by.trim()) { alert('Requested By is required'); return; }
    if (!disbursementForm.purpose.trim()) { alert('Purpose/description is required'); return; }
    setDisbursementLoading(true);
    try {
      const res = await axios.post(`${API}/harvest-disbursements`, {
        ...disbursementForm,
        amount: parseFloat(disbursementForm.amount),
        admin_role: adminUser?.role || ''
      });
      alert(`Disbursement ${res.data.disbursement.reference} issued successfully. Email sent to finance.`);
      setDisbursementForm({ harvest_event_id: '', amount: '', requested_by: '', purpose: '' });
      setShowDisbursementForm(false);
      loadDisbursements();
      loadContributions();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to issue disbursement');
    }
    setDisbursementLoading(false);
  };

  const [contributionForm, setContributionForm] = useState({
    harvest_event_id: '',
    harvest_type: 'adult',
    contributor_type: 'member',
    contributor_id: '',
    contributor_name: '',
    amount: '',
    payment_method: 'cash',
    received_by_id: adminUser?.id || '',
    received_by_name: adminUser?.full_name || '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [guestForm, setGuestForm] = useState({
    full_name: '',
    phone: '',
    email: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsRes, contribRes, membersRes, guestsRes, churchRes] = await Promise.all([
        axios.get(`${API}/harvest-events`),
        axios.get(`${API}/harvest-contributions`),
        axios.get(`${API}/members`),
        axios.get(`${API}/guest-contributors`),
        axios.get(`${API}/church`)
      ]);
      setEvents(eventsRes.data);
      setContributions(contribRes.data);
      setMembers(membersRes.data);
      setGuests(guestsRes.data);
      setChurch(churchRes.data);
      
      const activeEvent = eventsRes.data.find(e => e.is_active);
      if (activeEvent && !selectedEvent) {
        setSelectedEvent(activeEvent);
        setContributionForm(prev => ({ ...prev, harvest_event_id: activeEvent.id }));
      }
    } catch (error) {
      console.error('Error loading harvest data:', error);
    }
    setLoading(false);
  }, [selectedEvent]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEventSubmit = async () => {
    if (!isMainAdmin) {
      alert('Only Main Admin can create or edit projects');
      return;
    }
    if (!eventForm.name || !eventForm.date) {
      alert('Please fill in event name and date');
      return;
    }

    try {
      const payload = { ...eventForm, admin_role: adminUser?.role || '', admin_name: adminUser?.username || '' };
      if (editingEvent) {
        await axios.put(`${API}/harvest-events/${editingEvent.id}`, payload);
      } else {
        await axios.post(`${API}/harvest-events`, payload);
      }
      resetEventForm();
      loadData();
    } catch (error) {
      console.error('Error saving event:', error);
      alert(error.response?.data?.detail || 'Failed to save harvest event');
    }
  };

  const handleContributionSubmit = async () => {
    if (!contributionForm.contributor_name || !contributionForm.amount || !contributionForm.harvest_event_id) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      // Get contributor ID for display on receipt
      let contributorIdDisplay = '-';
      if (contributionForm.contributor_type === 'member') {
        const member = members.find(m => m.id === contributionForm.contributor_id);
        contributorIdDisplay = member?.member_number || '-';
      } else {
        const guest = guests.find(g => g.contributor_id === contributionForm.contributor_id);
        contributorIdDisplay = guest?.contributor_id || contributionForm.contributor_id || '-';
      }
      
      const response = await axios.post(`${API}/harvest-contributions`, {
        ...contributionForm,
        amount: parseFloat(contributionForm.amount)
      });
      
      // Show receipt immediately after recording
      if (response.data.success) {
        setReceiptData({
          receipt: {
            ...response.data.contribution,
            contributor_id_display: contributorIdDisplay
          },
          miniStatement: response.data.mini_statement,
          church: church
        });
        setShowReceipt(true);
      }
      
      resetContributionForm();
      loadData();
    } catch (error) {
      console.error('Error recording contribution:', error);
      alert('Failed to record contribution');
    }
  };

  const handleViewReceipt = async (receiptNumber) => {
    try {
      const response = await axios.get(`${API}/harvest-contributions/receipt/${receiptNumber}`);
      setReceiptData(response.data);
      setShowReceipt(true);
    } catch (error) {
      console.error('Error loading receipt:', error);
      alert('Failed to load receipt');
    }
  };

  const handleDownloadPDF = async (receiptNumber) => {
    try {
      const response = await axios.get(`${API}/harvest-contributions/receipt/${receiptNumber}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Receipt_${receiptNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  const handleGuestSubmit = async () => {
    if (!guestForm.full_name.trim()) {
      alert('Please enter guest full name');
      return;
    }
    if (!guestForm.phone.trim()) {
      alert('Please enter guest phone number');
      return;
    }
    if (!guestForm.email.trim()) {
      alert('Please enter guest email address');
      return;
    }

    try {
      const response = await axios.post(`${API}/guest-contributors`, guestForm);
      setGuests(prev => [...prev, response.data.contributor]);
      setContributionForm(prev => ({
        ...prev,
        contributor_type: 'guest',
        contributor_id: response.data.contributor.contributor_id,
        contributor_name: response.data.contributor.full_name
      }));
      setGuestForm({ full_name: '', phone: '', email: '' });
      setShowGuestForm(false);
    } catch (error) {
      console.error('Error creating guest:', error);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!isMainAdmin) {
      alert('Only Main Admin can delete projects');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this harvest project? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/harvest-events/${eventId}?admin_role=${adminUser?.role || ''}&admin_name=${adminUser?.username || ''}`);
      loadData();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const resetEventForm = () => {
    setEventForm({
      name: '',
      harvest_type: 'adult',
      date: new Date().toISOString().split('T')[0],
      description: '',
      target_amount: 0
    });
    setEditingEvent(null);
    setShowEventForm(false);
  };

  const resetContributionForm = () => {
    setContributionForm({
      harvest_event_id: selectedEvent?.id || '',
      harvest_type: 'adult',
      contributor_type: 'member',
      contributor_id: '',
      contributor_name: '',
      amount: '',
      payment_method: 'cash',
      received_by_id: adminUser?.id || '',
      received_by_name: adminUser?.full_name || '',
      notes: '',
      date: new Date().toISOString().split('T')[0]
    });
    setShowContributionForm(false);
  };

  const getEventStats = (eventId) => {
    const eventContribs = contributions.filter(c => c.harvest_event_id === eventId && !c.voided);
    const total = eventContribs.reduce((sum, c) => sum + (c.amount || 0), 0);
    return { count: eventContribs.length, total };
  };

  const filteredContributions = contributions.filter(c => {
    if (filterEventId && c.harvest_event_id !== filterEventId) return false;
    if (filterType && c.harvest_type !== filterType) return false;
    if (searchTerm && !c.contributor_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Helper to get event name by ID
  const getEventName = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event?.name || 'Unknown Event';
  };

  const totalHarvest = contributions.filter(c => !c.voided).reduce((sum, c) => sum + (c.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="church-projects-section">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-amber-500 to-yellow-500 rounded-3xl p-6 text-white shadow-xl"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
              <Wheat className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Church Projects</h2>
              <p className="text-amber-100">Manage projects, events and contributions</p>
            </div>
          </div>
          <div className="flex gap-3 items-center">
            {!isMainAdmin && (
              <span className="flex items-center gap-1 px-3 py-1 rounded-lg bg-white/20 text-xs font-medium">
                <Lock className="w-3 h-3" />
                View Only Edits
              </span>
            )}
            <motion.button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">£{totalHarvest.toLocaleString('en-GB')}</p>
            <p className="text-sm text-amber-200">Total Harvest</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{events.filter(e => e.is_active).length}</p>
            <p className="text-sm text-amber-200">Active Projects</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{contributions.filter(c => !c.voided).length}</p>
            <p className="text-sm text-amber-200">Contributions</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{guests.length}</p>
            <p className="text-sm text-amber-200">Guest Contributors</p>
          </div>
        </div>

        {/* Per-Project Totals */}
        {events.length > 0 && (
          <div className="mt-4">
            <p className="text-sm text-amber-200 mb-2 font-medium">Per-Project Totals</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {events.filter(e => e.is_active).map(event => {
                const stats = getEventStats(event.id);
                return (
                  <div
                    key={event.id}
                    className="bg-white/15 rounded-lg p-3 backdrop-blur-sm cursor-pointer hover:bg-white/25 transition-colors"
                    onClick={() => {
                      setSelectedEvent(event);
                      setContributionForm(prev => ({ ...prev, harvest_event_id: event.id }));
                      setActiveTab('record');
                    }}
                    data-testid={`project-stat-${event.id}`}
                  >
                    <p className="text-lg font-bold">£{stats.total.toLocaleString('en-GB')}</p>
                    <p className="text-xs text-amber-200 truncate" title={event.name}>{event.name}</p>
                    <p className="text-[10px] text-amber-300">{stats.count} contribution{stats.count !== 1 ? 's' : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
        <div className="flex gap-2 overflow-x-auto">
          {[
            { id: 'events', label: 'Projects', icon: Calendar },
            { id: 'contributions', label: 'Contributions', icon: Receipt },
            { id: 'record', label: 'Record New', icon: Plus },
            { id: 'disbursements', label: 'Disbursements', icon: ArrowUpRight },
            { id: 'reports', label: 'Reports', icon: FileSpreadsheet }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-amber-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Events Tab */}
      {activeTab === 'events' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Church Projects</h3>
            {isMainAdmin && (
              <motion.button
                onClick={() => setShowEventForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="new-project-btn"
              >
                <Plus className="w-4 h-4" />
                New Project
              </motion.button>
            )}
          </div>

          {loading ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-500">Loading events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
              <Wheat className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-700 mb-2">No Projects Yet</h3>
              <p className="text-slate-500">Create your first harvest event to get started</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {events.map((event, index) => {
                const stats = getEventStats(event.id);
                const typeInfo = HARVEST_TYPES.find(t => t.value === event.harvest_type) || HARVEST_TYPES[0];
                const progress = event.target_amount > 0 ? (stats.total / event.target_amount) * 100 : 0;

                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-white rounded-2xl p-5 shadow-lg border-2 cursor-pointer ${
                      event.is_active ? 'border-amber-200' : 'border-slate-200 opacity-60'
                    } ${selectedEvent?.id === event.id ? 'ring-2 ring-amber-500' : ''}`}
                    onClick={() => {
                      setSelectedEvent(event);
                      setContributionForm(prev => ({ ...prev, harvest_event_id: event.id }));
                      setActiveTab('record');
                    }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          typeInfo.value === 'adult' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {typeInfo.label}
                        </span>
                        <h4 className="font-bold text-slate-800 mt-2">{event.name}</h4>
                        <p className="text-sm text-slate-500">{event.date}</p>
                      </div>
                      {isMainAdmin && (
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEventForm({
                                name: event.name,
                                harvest_type: event.harvest_type,
                                date: event.date,
                                description: event.description || '',
                                target_amount: event.target_amount || 0
                              });
                              setEditingEvent(event);
                              setShowEventForm(true);
                            }}
                            className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEvent(event.id);
                            }}
                            className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{stats.count} contributions</span>
                        <span className="font-bold text-amber-600">£{stats.total.toLocaleString('en-GB')}</span>
                      </div>
                      {event.target_amount > 0 && (
                        <>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-amber-400 to-yellow-400 rounded-full transition-all"
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-slate-500 text-right">
                            {progress.toFixed(1)}% of £{event.target_amount.toLocaleString('en-GB')} target
                          </p>
                        </>
                      )}
                      <p className="text-xs text-amber-500 font-medium mt-1 flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Click to record contribution
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Contributions Tab */}
      {activeTab === 'contributions' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search contributor..."
                    className="w-full pl-10 pr-4 py-2 border-2 border-slate-200 rounded-xl focus:border-amber-400 outline-none"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <select
                className="px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-amber-400 outline-none"
                value={filterEventId}
                onChange={e => setFilterEventId(e.target.value)}
              >
                <option value="">All Events</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
              <select
                className="px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-amber-400 outline-none"
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
              >
                <option value="">All Types</option>
                {HARVEST_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {filteredContributions.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
              <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-700 mb-2">No Contributions</h3>
              <p className="text-slate-500">No contributions recorded yet</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Receipt #</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Contributor</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Project</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Received By</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredContributions.map((contrib) => {
                      const typeInfo = HARVEST_TYPES.find(t => t.value === contrib.harvest_type) || HARVEST_TYPES[0];
                      // Get contributor ID
                      const contributorId = contrib.contributor_type === 'member'
                        ? members.find(m => m.id === contrib.contributor_id)?.member_number || '-'
                        : guests.find(g => g.contributor_id === contrib.contributor_id)?.contributor_id || contrib.contributor_id || '-';
                      return (
                        <tr key={contrib.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-mono text-slate-600">{contrib.receipt_number}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-lg text-xs font-mono font-medium ${
                              contrib.contributor_type === 'member' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {contributorId}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                contrib.contributor_type === 'guest' ? 'bg-purple-100' : 'bg-blue-100'
                              }`}>
                                {contrib.contributor_type === 'guest' ? (
                                  <UserPlus className="w-4 h-4 text-purple-600" />
                                ) : (
                                  <User className="w-4 h-4 text-blue-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{contrib.contributor_name}</p>
                                <p className="text-xs text-slate-500">{contrib.date}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-600 max-w-[150px] truncate block" title={getEventName(contrib.harvest_event_id)}>
                              {getEventName(contrib.harvest_event_id)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              typeInfo.value === 'adult' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {typeInfo.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-bold text-amber-600">£{contrib.amount?.toLocaleString('en-GB')}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{contrib.received_by_name || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleViewReceipt(contrib.receipt_number)}
                                className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                                title="View Receipt"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadPDF(contrib.receipt_number)}
                                className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100"
                                title="Download PDF"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleViewReceipt(contrib.receipt_number)}
                                className="p-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100"
                                title="Reprint Receipt"
                              >
                                <Printer className="w-4 h-4" />
                              </button>
                              {isMainAdmin && !contrib.voided && (
                                <button
                                  onClick={() => setVoidModal({ show: true, contribution: contrib, reason: '', loading: false })}
                                  className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                                  title="Void Contribution"
                                  data-testid={`void-btn-${contrib.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              {contrib.voided && (
                                <span className="px-2 py-1 rounded-lg text-xs font-bold bg-red-100 text-red-700">VOIDED</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Record New Tab */}
      {activeTab === 'record' && (
        <div className="space-y-4">
          {/* Mobile QR Code Card */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-5 shadow-lg flex items-center gap-5">
            <div className="bg-white p-2 rounded-xl flex-shrink-0">
              <QRCodeSVG
                value="https://cccvob.com/harvest-record"
                size={90}
                level="M"
                bgColor="#ffffff"
                fgColor="#1e293b"
              />
            </div>
            <div className="text-white">
              <h4 className="font-bold text-sm">Record on Mobile</h4>
              <p className="text-slate-300 text-xs mt-1">Scan QR code or share the link to record contributions from your phone</p>
              <button
                onClick={() => {
                  const url = 'https://cccvob.com/harvest-record';
                  navigator.clipboard?.writeText(url).then(() => alert('Link copied!')).catch(() => window.prompt('Copy this link:', url));
                }}
                className="mt-2 text-xs font-medium text-amber-400 hover:text-amber-300 flex items-center gap-1"
              >
                <FileText className="w-3 h-3" />
                Copy Link
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
          <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-600" />
            Record New Contribution
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Select Project *</label>
              <select
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                value={contributionForm.harvest_event_id}
                onChange={e => setContributionForm(prev => ({ ...prev, harvest_event_id: e.target.value }))}
              >
                <option value="">Select event...</option>
                {events.filter(e => e.is_active).map(event => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Harvest Type</label>
              <select
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                value={contributionForm.harvest_type}
                onChange={e => setContributionForm(prev => ({ ...prev, harvest_type: e.target.value }))}
              >
                {HARVEST_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Contributor Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setContributionForm(prev => ({ ...prev, contributor_type: 'member', contributor_id: '', contributor_name: '' }))}
                  className={`flex-1 py-2 rounded-xl font-medium transition-all ${
                    contributionForm.contributor_type === 'member'
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <User className="w-4 h-4 inline mr-1" /> Member
                </button>
                <button
                  type="button"
                  onClick={() => setContributionForm(prev => ({ ...prev, contributor_type: 'guest', contributor_id: '', contributor_name: '' }))}
                  className={`flex-1 py-2 rounded-xl font-medium transition-all ${
                    contributionForm.contributor_type === 'guest'
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <UserPlus className="w-4 h-4 inline mr-1" /> Guest
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                {contributionForm.contributor_type === 'member' ? 'Select Member *' : 'Select Guest *'}
              </label>
              <div className="flex gap-2">
                <select
                  className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                  value={contributionForm.contributor_id}
                  onChange={e => {
                    const selected = contributionForm.contributor_type === 'member'
                      ? members.find(m => m.id === e.target.value)
                      : guests.find(g => g.contributor_id === e.target.value);
                    setContributionForm(prev => ({
                      ...prev,
                      contributor_id: e.target.value,
                      contributor_name: selected?.full_name || selected?.name || ''
                    }));
                  }}
                >
                  <option value="">Select...</option>
                  {contributionForm.contributor_type === 'member'
                    ? members.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.member_number ? `[${m.member_number}] ` : ''}{m.name || m.full_name}
                        </option>
                      ))
                    : guests.map(g => (
                        <option key={g.contributor_id} value={g.contributor_id}>
                          [{g.contributor_id}] {g.full_name}
                        </option>
                      ))
                  }
                </select>
                {contributionForm.contributor_type === 'guest' && (
                  <button
                    type="button"
                    onClick={() => setShowGuestForm(true)}
                    className="px-3 py-2 rounded-xl bg-purple-100 text-purple-600 hover:bg-purple-200"
                    title="Add New Guest"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Amount (£) *</label>
              <div className="relative">
                <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="number"
                  className="w-full border-2 border-slate-200 rounded-xl pl-10 pr-4 py-3 focus:border-amber-400 outline-none"
                  placeholder="0.00"
                  value={contributionForm.amount}
                  onChange={e => setContributionForm(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Method</label>
              <select
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                value={contributionForm.payment_method}
                onChange={e => setContributionForm(prev => ({ ...prev, payment_method: e.target.value }))}
              >
                {PAYMENT_METHODS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Date</label>
              <input
                type="date"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                value={contributionForm.date}
                onChange={e => setContributionForm(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Notes</label>
              <input
                type="text"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                placeholder="Optional notes..."
                value={contributionForm.notes}
                onChange={e => setContributionForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>

          {/* Received By Info */}
          <div className="mt-4 p-3 bg-slate-50 rounded-xl flex items-center gap-2">
            <User className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-600">Received by: <span className="font-semibold">{adminUser?.full_name || 'Admin'}</span></span>
          </div>

          <motion.button
            onClick={handleContributionSubmit}
            className="mt-6 w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <CheckCircle2 className="w-5 h-5" />
            Record Contribution & Generate Receipt
          </motion.button>
        </div>
        </div>
      )}

      {/* Disbursements Tab */}
      {activeTab === 'disbursements' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Fund Disbursements</h3>
            {isMainAdmin && (
              <motion.button
                onClick={() => setShowDisbursementForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-white font-medium shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="new-disbursement-btn"
              >
                <ArrowUpRight className="w-4 h-4" />
                Issue Disbursement
              </motion.button>
            )}
            {!isMainAdmin && (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Lock className="w-4 h-4" />
                Main Admin only
              </div>
            )}
          </div>

          {/* Disbursement Records */}
          {disbursements.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center shadow-lg border border-slate-100">
              <ArrowUpRight className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No disbursements recorded yet</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase">Reference</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase">Project</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase">Requested By</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase">Issued By</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase">Purpose</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase">Balance After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disbursements.map(d => (
                      <tr key={d.id} className="border-b border-slate-100 hover:bg-amber-50/30">
                        <td className="p-3 font-mono text-sm font-bold text-amber-700">{d.reference}</td>
                        <td className="p-3 text-sm font-medium text-slate-800">{d.harvest_event_name}</td>
                        <td className="p-3 text-sm font-bold text-red-600">-£{d.amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                        <td className="p-3 text-sm text-slate-700">{d.requested_by}</td>
                        <td className="p-3 text-sm text-slate-700">{d.issued_by}</td>
                        <td className="p-3 text-sm text-slate-600 max-w-[200px] truncate" title={d.purpose}>{d.purpose}</td>
                        <td className="p-3 text-sm text-slate-500">{d.date}</td>
                        <td className="p-3 text-sm font-semibold text-emerald-600">£{d.balance_after?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <ContributionReports 
          events={events} 
          contributions={contributions} 
          members={members}
          guests={guests}
        />
      )}

      {/* Event Form Modal */}
      <AnimatePresence>
        {showEventForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => resetEventForm()}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-yellow-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">
                    {editingEvent ? 'Edit Project' : 'New Project'}
                  </h3>
                  <button onClick={() => resetEventForm()} className="p-2 rounded-xl hover:bg-slate-100">
                    <X className="w-6 h-6 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Event Name *</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                    placeholder="e.g., Annual Harvest 2026"
                    value={eventForm.name}
                    onChange={e => setEventForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Type</label>
                    <select
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                      value={eventForm.harvest_type}
                      onChange={e => setEventForm(prev => ({ ...prev, harvest_type: e.target.value }))}
                    >
                      {HARVEST_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Date *</label>
                    <input
                      type="date"
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                      value={eventForm.date}
                      onChange={e => setEventForm(prev => ({ ...prev, date: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Target Amount (£)</label>
                  <input
                    type="number"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                    placeholder="0"
                    value={eventForm.target_amount}
                    onChange={e => setEventForm(prev => ({ ...prev, target_amount: parseFloat(e.target.value) || 0 }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                  <textarea
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none resize-none"
                    rows={3}
                    placeholder="Event description..."
                    value={eventForm.description}
                    onChange={e => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => resetEventForm()}
                    className="flex-1 py-3 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <motion.button
                    onClick={handleEventSubmit}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-bold shadow-lg"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {editingEvent ? 'Update Project' : 'Create Project'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guest Form Modal */}
      <AnimatePresence>
        {showGuestForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowGuestForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">Add Guest Contributor</h3>
                  <button onClick={() => setShowGuestForm(false)} className="p-2 rounded-xl hover:bg-slate-100">
                    <X className="w-6 h-6 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-purple-400 outline-none"
                    placeholder="Guest's full name"
                    value={guestForm.full_name}
                    onChange={e => setGuestForm(prev => ({ ...prev, full_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
                  <input
                    type="tel"
                    required
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-purple-400 outline-none"
                    placeholder="Phone number"
                    value={guestForm.phone}
                    onChange={e => setGuestForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-purple-400 outline-none"
                    placeholder="Email address"
                    value={guestForm.email}
                    onChange={e => setGuestForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowGuestForm(false)}
                    className="flex-1 py-3 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <motion.button
                    onClick={handleGuestSubmit}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold shadow-lg"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Add Guest
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Receipt Modal */}
      <AnimatePresence>
        {showReceipt && receiptData && (
          <ReceiptModal
            receipt={receiptData.receipt}
            miniStatement={receiptData.mini_statement || receiptData.miniStatement}
            church={receiptData.church}
            onClose={() => {
              setShowReceipt(false);
              setReceiptData(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Disbursement Form Modal */}
      <AnimatePresence>
        {showDisbursementForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !disbursementLoading && setShowDisbursementForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <ArrowUpRight className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Issue Fund Disbursement</h3>
                  <p className="text-sm text-slate-500">Release funds from a harvest project</p>
                </div>
                <button onClick={() => setShowDisbursementForm(false)} className="ml-auto text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Project *</label>
                  <select
                    value={disbursementForm.harvest_event_id}
                    onChange={e => setDisbursementForm(prev => ({ ...prev, harvest_event_id: e.target.value }))}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                    data-testid="disbursement-event-select"
                  >
                    <option value="">Select harvest project...</option>
                    {events.map(ev => (
                      <option key={ev.id} value={ev.id}>{ev.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Amount (£) *</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={disbursementForm.amount}
                    onChange={e => setDisbursementForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                    placeholder="Enter amount"
                    data-testid="disbursement-amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Requested By *</label>
                  <input
                    type="text"
                    value={disbursementForm.requested_by}
                    onChange={e => setDisbursementForm(prev => ({ ...prev, requested_by: e.target.value }))}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none"
                    placeholder="Name of person requesting funds"
                    data-testid="disbursement-requested-by"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Purpose / Description *</label>
                  <textarea
                    value={disbursementForm.purpose}
                    onChange={e => setDisbursementForm(prev => ({ ...prev, purpose: e.target.value }))}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-amber-400 outline-none resize-none"
                    rows={3}
                    placeholder="e.g. Payment to contractor for roof tiles"
                    data-testid="disbursement-purpose"
                  />
                </div>

                <p className="text-xs text-slate-500">
                  An email notification will be sent to finance@cccvob.com with disbursement details.
                </p>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowDisbursementForm(false)}
                    className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                    disabled={disbursementLoading}
                  >
                    Cancel
                  </button>
                  <motion.button
                    onClick={handleDisbursement}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={disbursementLoading}
                    data-testid="disbursement-submit"
                  >
                    {disbursementLoading ? 'Processing...' : 'Issue Disbursement'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Void Contribution Modal */}
      <AnimatePresence>
        {voidModal.show && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !voidModal.loading && setVoidModal({ show: false, contribution: null, reason: '', loading: false })}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-700">Void Contribution</h3>
                  <p className="text-sm text-slate-500">This action cannot be undone</p>
                </div>
              </div>

              {voidModal.contribution && (
                <div className="bg-red-50 rounded-xl p-4 mb-4 border border-red-100">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Receipt:</span>
                    <span className="font-mono font-bold text-slate-800">{voidModal.contribution.receipt_number}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Contributor:</span>
                    <span className="font-medium text-slate-800">{voidModal.contribution.contributor_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Amount:</span>
                    <span className="font-bold text-red-600">£{voidModal.contribution.amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Reason for Voiding *</label>
                <textarea
                  value={voidModal.reason}
                  onChange={e => setVoidModal(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full border-2 border-red-200 rounded-xl px-4 py-3 focus:border-red-400 outline-none resize-none"
                  rows={3}
                  placeholder="Enter reason for voiding this contribution..."
                  data-testid="void-reason-input"
                />
              </div>

              <p className="text-xs text-slate-500 mb-4">
                An email notification will be sent to finance@cccvob.com and the contributor.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setVoidModal({ show: false, contribution: null, reason: '', loading: false })}
                  className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                  disabled={voidModal.loading}
                >
                  Cancel
                </button>
                <motion.button
                  onClick={handleVoidContribution}
                  className="flex-1 py-3 rounded-xl bg-red-600 text-white font-bold shadow-lg disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={voidModal.loading || !voidModal.reason.trim()}
                  data-testid="void-confirm-btn"
                >
                  {voidModal.loading ? 'Voiding...' : 'Void Contribution'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HarvestSection;
